/**
 * Outsource Partner Portal — Dashboard (Phase 2).
 * Lists loan cases assigned to the signed-in outsource partner.
 * Wraps the standalone header used across the outsource portal pages.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Handshake, LogOut, ArrowRight, Loader2, Users } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  under_review: 'bg-blue-100 text-blue-700',
  kiv: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  withdrawn: 'bg-gray-100 text-gray-500',
};

export default function OutsourceDashboard() {
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  const token = () => localStorage.getItem('outsource_token');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API_BASE}/api/outsource/cases`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setItems(r.data.items || []);
      setPartner({ id: r.data.partner_id, name: r.data.partner_name });
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem('outsource_token');
        localStorage.removeItem('outsource_profile');
        navigate('/outsource/login');
        return;
      }
      toast.error(e.response?.data?.detail || 'Failed to load cases');
    } finally { setBusy(false); }
  }, [navigate]);

  useEffect(() => {
    if (!token()) { navigate('/outsource/login'); return; }
    const raw = localStorage.getItem('outsource_profile');
    if (raw) { try { setPartner(JSON.parse(raw)); } catch { /* noop */ } }
    load();
  }, [navigate, load]);

  const logout = () => {
    localStorage.removeItem('outsource_token');
    localStorage.removeItem('outsource_profile');
    navigate('/outsource/login');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="outsource-dashboard">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Handshake className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Outsource Partner Portal</h1>
              <p className="text-xs text-gray-500">Signed in as {partner?.name || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/outsource/roster')} data-testid="outsource-manage-roster-btn">
              <Users className="w-4 h-4 mr-1" /> Manage roster
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="outsource-logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border p-4 md:p-6">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-semibold text-gray-800">Loan Cases assigned to us ({items.length})</h2>
            <Button size="sm" variant="ghost" onClick={load} data-testid="outsource-reload-btn">Refresh</Button>
          </div>
          {busy ? (
            <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-gray-400">
              No cases have been assigned to your agency yet. When SAM Rumah admins route a Type&nbsp;B loan case to you, it will appear here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-gray-500">
                  <tr>
                    <th className="text-left p-2">Case Ref</th>
                    <th className="text-left p-2">Customer</th>
                    <th className="text-left p-2">Property</th>
                    <th className="text-right p-2">Loan Amount</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-center p-2">Bankers</th>
                    <th className="text-left p-2">Received</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(c => (
                    <tr key={c.id} className="border-t hover:bg-slate-50" data-testid={`outsource-case-row-${c.id}`}>
                      <td className="p-2 font-mono text-xs">{c.case_ref || c.id.slice(0, 8)}</td>
                      <td className="p-2">{c.customer_name || '—'}<span className="text-xs text-gray-400 block">{c.customer_ic || ''}</span></td>
                      <td className="p-2 text-xs">{c.property_name || '—'}{c.unit_ref ? ` · #${c.unit_ref}` : ''}</td>
                      <td className="p-2 text-right text-xs">{c.loan_amount_requested ? `RM ${Number(c.loan_amount_requested).toLocaleString('en-MY')}` : '—'}</td>
                      <td className="p-2"><Badge className={STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-600'}>{c.status}</Badge></td>
                      <td className="p-2 text-center">
                        {c.assignments_count > 0 ? (
                          <Badge className="bg-indigo-100 text-indigo-700">Assigned to {c.assignments_count} banker{c.assignments_count > 1 ? 's' : ''}</Badge>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                      <td className="p-2 text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString('en-MY') : ''}</td>
                      <td className="p-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/outsource/cases/${c.id}`)} data-testid={`outsource-case-open-${c.id}`}>
                          Open <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
